import { ExercisePhase } from '@pelckmans/business-components/components/exercise-manager/exercise-types';
import { useContext } from 'react';

import useMedialinks from '../medialink/useMedialinks';
import useUserMaterial from '../user-material/useUserMaterial';

import ModalContext from '../../../../../contexts/ModalContext';
import ModuleContext from '../../../_contexts/ModuleContext';

import { ValuesOfObject } from '../../../../../@types/utils';
import {
  CompletedAssignment,
  ExpiredAssignment,
  OpenAssignment,
  type MyAssignment,
} from '../../../../../api/assignment';
import { getMedialinkById } from '../../../../../api/medialink';
import { getModuleById } from '../../../../../api/module';
import { getMaterialByAssignment } from '../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';

export default function useAssignmentHandling({
  onAssignmentClosed,
  subLocationId,
}: {
  onAssignmentClosed: () => Promise<void>;
  subLocationId: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>;
}) {
  const { module, selectedModule } = useContext(ModuleContext);
  const { showModal } = useContext(ModalContext);

  const { openMedialink, openReview } = useMedialinks();
  const { openMaterial } = useUserMaterial();
  const handleError = useThrowAsyncError();

  async function openAssignment(
    assignment: CompletedAssignment | OpenAssignment | MyAssignment,
    phase?: ExercisePhase,
  ) {
    try {
      if (!selectedModule) return;

      const assignmentModule = module || (await getModuleById(assignment.moduleId));

      if (assignment.materialType === 'medialink') {
        const medialink = await getMedialinkById(assignmentModule.id, assignment.materialId, {
          includeAllGroups: true,
          includeSubModules: true,
        });

        const node = medialink.hierarchy[medialink.hierarchy.length - 1];
        const modal = await openMedialink(
          medialink,
          node,
          assignmentModule,
          selectedModule,
          onAssignmentClosed,
          phase,
          subLocationId,
        );

        if (modal) showModal(modal);

        return;
      }

      const usm = await getMaterialByAssignment(assignment);

      const modal = await openMaterial(usm, onAssignmentClosed, subLocationId);

      if (modal) showModal(modal);
    } catch (err) {
      handleError(err);
    }
  }

  async function reviewAssignment(assignment: CompletedAssignment | ExpiredAssignment) {
    if (!module || !selectedModule) return;

    if (assignment.materialType === 'medialink') {
      const medialink = await getMedialinkById(module.id, assignment.materialId, {
        includeAllGroups: true,
        includeSubModules: true,
      });

      const node = medialink.hierarchy[medialink.hierarchy.length - 1];
      const modal = await openReview(medialink, assignment.sessionId, module, node, selectedModule, subLocationId);
      if (modal) showModal(modal);

      return;
    }

    // expired material assignments and lti are not reviewable when expired.
    if (!('owner' in assignment)) return;

    const usm = await getMaterialByAssignment(assignment);

    const modal = await openMaterial(usm, undefined, subLocationId);

    if (modal) showModal(modal);
  }

  return {
    startAssignment: (assignment: CompletedAssignment | OpenAssignment | MyAssignment) =>
      openAssignment(assignment, 'start'),
    restartAssignment: (assignment: CompletedAssignment | OpenAssignment | MyAssignment) =>
      openAssignment(assignment, 'start'),
    resumeAssignment: (assignment: CompletedAssignment | OpenAssignment | MyAssignment) =>
      openAssignment(assignment, 'resume'),
    reviewAsssignment: (assignment: CompletedAssignment | ExpiredAssignment) => reviewAssignment(assignment),
  };
}
